/**
 * CMS-03 — songs table. Every filter (status, tier, language, genre, artist, free text) and both
 * paging and sorting are **server-side** (docs/06 §4): the catalog is 143k rows, so nothing is
 * filtered in the browser.
 */
import type {
  CmsArtistsResponse,
  CmsSongDTO,
  CmsSongGenresResponse,
  CmsSongsQueryInput,
  CmsSongsResponse,
  MatchStatus,
  SongLanguage,
  SongStatus,
  Tier,
} from '@akpl/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { MatchBadge, StatusBadge, TierBadge } from '../components/catalog/badges';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Input, Label, Select } from '../components/ui/field';
import { Badge } from '../components/ui/panel';
import { useT } from '../i18n';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatNumber, formatStamp } from '../lib/format';

export interface SongFilters {
  q: string;
  status: SongStatus | '';
  tier: Tier | '';
  language: SongLanguage | '';
  genre: string;
  artistId: string;
  matchStatus: MatchStatus | '';
}

export const EMPTY_FILTERS: SongFilters = {
  q: '',
  status: '',
  tier: '',
  language: '',
  genre: '',
  artistId: '',
  matchStatus: '',
};

const SORT_IDS = new Set(['title', 'relevance', 'status', 'tier', 'updated_at', 'created_at']);

export function toSongsQuery(
  filters: SongFilters,
  page: number,
  sorting: SortingState,
): CmsSongsQueryInput {
  const query: CmsSongsQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.status) query.status = filters.status;
  if (filters.tier) query.tier = filters.tier;
  if (filters.language) query.language = filters.language;
  if (filters.genre) query.genre = filters.genre;
  if (filters.artistId) query.artistId = filters.artistId;
  if (filters.matchStatus) query.matchStatus = filters.matchStatus;
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

/** The 24 px flat swatch CMS-03 draws in front of every row (no cover art in the catalog yet). */
function Cover({ song }: { song: CmsSongDTO }) {
  if (song.imageUrl) {
    return <img src={song.imageUrl} alt="" className="size-8 rounded-md object-cover" />;
  }
  const hue = [...song.id].reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) % 360, 7);
  return (
    <span
      aria-hidden="true"
      className="block size-8 rounded-md"
      style={{ backgroundColor: `hsl(${hue} 70% 62%)` }}
    />
  );
}

export function Songs() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const navigate = useNavigate();
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');

  const [filters, setFilters] = useState<SongFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);

  const query = toSongsQuery(filters, page, sorting);
  const result = useQuery<CmsSongsResponse>({
    queryKey: ['cms', 'songs', query],
    queryFn: () => api.cmsSongs({ query }),
    placeholderData: keepPreviousData,
  });

  // The artist filter is a picker over the most relevant artists, not the full 27k list.
  const artists = useQuery<CmsArtistsResponse>({
    queryKey: ['cms', 'artists', 'filter'],
    queryFn: () => api.cmsArtists({ query: { pageSize: 100, sort: 'song_count', dir: 'desc' } }),
    staleTime: 60_000,
  });

  // The genre column is free text normalized by the catalog tools, not an enum, so the filter
  // asks the catalog which values exist (QA3 CMS-63).
  const genres = useQuery<CmsSongGenresResponse>({
    queryKey: ['cms', 'songs', 'genres'],
    queryFn: () => api.cmsSongGenres(),
    staleTime: 300_000,
  });

  // "N en revisión" chip: one extra count query, not a client-side scan of the page.
  const inReview = useQuery<CmsSongsResponse>({
    queryKey: ['cms', 'songs', 'review-count'],
    queryFn: () => api.cmsSongs({ query: { matchStatus: 'review', pageSize: 1 } }),
    staleTime: 60_000,
  });

  const rows = result.data?.items ?? [];

  const columns = useMemo<ColumnDef<CmsSongDTO>[]>(
    () => [
      {
        id: 'title',
        header: t('cms:songs.columns.title'),
        cell: ({ row }) => (
          <span className="flex items-center gap-3">
            <Cover song={row.original} />
            <span className="min-w-0">
              <span className="block truncate font-semibold text-ink">{row.original.title}</span>
              <span className="block truncate text-[12px] text-muted">
                {row.original.artists.length > 0
                  ? row.original.artists.map((a) => a.name).join(' & ')
                  : t('cms:songs.noArtist')}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: 'language',
        header: t('cms:songs.columns.language'),
        cell: ({ row }) =>
          row.original.language ? (
            <Badge tone="cyan">{row.original.language.toUpperCase()}</Badge>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        id: 'genre',
        header: t('cms:songs.columns.genre'),
        cell: ({ row }) => <span className="text-muted">{row.original.genre ?? '—'}</span>,
      },
      {
        id: 'tier',
        header: t('cms:songs.columns.tier'),
        cell: ({ row }) => <TierBadge tier={row.original.tier} />,
      },
      {
        id: 'tones',
        header: t('cms:songs.columns.tones'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted">
            {t('cms:songs.toneCount', { count: row.original.tones.length })}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('cms:songs.columns.status'),
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'external',
        header: t('cms:songs.columns.external'),
        cell: ({ row }) => <MatchBadge status={row.original.external.matchStatus} />,
      },
      {
        id: 'relevance',
        header: t('cms:songs.columns.relevance'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.relevance}</span>,
      },
      {
        id: 'updated_at',
        header: t('cms:songs.columns.updated'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted">
            {formatStamp(row.original.updatedAt, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<CmsSongDTO>[] = [
    { header: 'id', value: (s) => s.id },
    { header: 'title', value: (s) => s.title },
    { header: 'artists', value: (s) => s.artists.map((a) => a.name).join(' & ') },
    { header: 'language', value: (s) => s.language ?? '' },
    { header: 'genre', value: (s) => s.genre ?? '' },
    { header: 'tier', value: (s) => s.tier },
    { header: 'tones', value: (s) => s.tones.map((tone) => tone.name).join(' ') },
    { header: 'status', value: (s) => s.status },
    { header: 'relevance', value: (s) => s.relevance },
    { header: 'source_id', value: (s) => s.sourceId ?? '' },
    { header: 'isrc', value: (s) => s.external.isrc ?? '' },
    { header: 'match_status', value: (s) => s.external.matchStatus ?? '' },
    { header: 'updated_at', value: (s) => s.updatedAt },
  ];

  const update = (patch: Partial<SongFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const total = result.data?.total ?? 0;
  const reviewCount = inReview.data?.total ?? 0;
  /**
   * There is no count until the query answers. Rendering `total ?? 0` made the header claim
   * "0 canciones" for the ~4 s the first page takes on a 143k catalog (QA 2026-09-15 UX-201).
   * A filter change keeps the previous count (`keepPreviousData`) and only marks it stale.
   */
  const hasCount = result.data !== undefined;

  return (
    <>
      <PageHeader
        title={t('cms:songs.title')}
        subtitle={
          hasCount ? (
            <span aria-busy={result.isPlaceholderData || undefined}>
              {t('cms:songs.subtitle', { total: formatNumber(total, locale) })}
            </span>
          ) : result.isError ? (
            t('cms:songs.subtitle', { total: '—' })
          ) : (
            t('cms:songs.subtitleLoading')
          )
        }
        actions={
          canEdit ? (
            <Button asChild>
              <Link to="/songs/new">+ {t('cms:songs.new')}</Link>
            </Button>
          ) : null
        }
      />

      <div className="px-6 pb-8">
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          label={t('cms:songs.title')}
          total={total}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={(row) => void navigate({ to: '/songs/$songId', params: { songId: row.id } })}
          rowLabel={(row) => t('cms:table.openRow', { name: row.title })}
          sortableIds={SORT_IDS}
          csv={{
            filename: 'akapella-songs.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsSongs({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <Input
                className="w-64"
                placeholder={t('cms:songs.searchPlaceholder')}
                aria-label={t('cms:songs.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-status">{t('cms:songs.filters.status')}</Label>
                <Select
                  id="f-status"
                  className="w-36"
                  value={filters.status}
                  onChange={(e) => update({ status: e.target.value as SongStatus | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(['draft', 'ready', 'published', 'archived'] as const).map((status) => (
                    <option key={status} value={status}>
                      {t(`cms:status.${status}` as 'cms:status.draft')}
                    </option>
                  ))}
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-tier">{t('cms:songs.filters.tier')}</Label>
                <Select
                  id="f-tier"
                  className="w-32"
                  value={filters.tier}
                  onChange={(e) => update({ tier: e.target.value as Tier | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(['standard', 'recent'] as const).map((tier) => (
                    <option key={tier} value={tier}>
                      {t(`common:tier.${tier}` as 'common:tier.standard')}
                    </option>
                  ))}
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-language">{t('cms:songs.filters.language')}</Label>
                <Select
                  id="f-language"
                  className="w-32"
                  value={filters.language}
                  onChange={(e) => update({ language: e.target.value as SongLanguage | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(['es', 'en', 'pt'] as const).map((code) => (
                    <option key={code} value={code}>
                      {t(`cms:songs.languages.${code}` as 'cms:songs.languages.es')}
                    </option>
                  ))}
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-genre">{t('cms:songs.filters.genre')}</Label>
                <Select
                  id="f-genre"
                  className="w-40"
                  value={filters.genre}
                  onChange={(e) => update({ genre: e.target.value })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(genres.data?.genres ?? []).map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-artist">{t('cms:songs.filters.artist')}</Label>
                <Select
                  id="f-artist"
                  className="w-44"
                  value={filters.artistId}
                  onChange={(e) => update({ artistId: e.target.value })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(artists.data?.items ?? []).map((artist) => (
                    <option key={artist.id} value={artist.id}>
                      {artist.name}
                    </option>
                  ))}
                </Select>
              </span>
              <span className="flex items-center gap-1.5">
                <Label htmlFor="f-match">{t('cms:songs.filters.match')}</Label>
                <Select
                  id="f-match"
                  className="w-36"
                  value={filters.matchStatus}
                  onChange={(e) => update({ matchStatus: e.target.value as MatchStatus | '' })}
                >
                  <option value="">{t('cms:songs.filters.all')}</option>
                  {(['matched', 'review', 'unmatched'] as const).map((status) => (
                    <option key={status} value={status}>
                      {t(`cms:songs.match.${status}` as 'cms:songs.match.matched')}
                    </option>
                  ))}
                </Select>
              </span>
              {reviewCount > 0 && (
                <Badge tone="warn" data-testid="review-chip">
                  {t('cms:songs.reviewChip', { count: reviewCount })}
                </Badge>
              )}
              <Button variant="link" size="sm" onClick={() => update(EMPTY_FILTERS)}>
                {t('cms:songs.clearFilters')}
              </Button>
            </>
          }
        />
      </div>
    </>
  );
}
